import { DOCUMENT, inject, Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";

import { pageLabelKeyFor } from "@features/shell/shell-nav";
import { TranslateService } from "@ngx-translate/core";

/**
 * How long after a dialog closes a notification still counts as coming from
 * it. Saves close their dialog first and notify second - often after the
 * request returns - so "the dialog that is open now" is usually none.
 */
const RECENTLY_CLOSED_MS = 10000;

/**
 * Where a notification was raised: the page, and the dialog the user was
 * working in.
 *
 * "Saved" or "Not saved" on its own does not say what was saved; the same
 * DATA_SAVED_SUCCESSFULLY is raised from both the groups and the
 * employees pages. Recording the place at
 * the moment of raising it answers that for every caller, including ones
 * written later, without each having to pass it in.
 */
@Injectable({ providedIn: "root" })
export class NotificationContextService {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  private readonly titles = new WeakMap<MatDialogRef<unknown>, string>();
  private lastClosed: { title: string; at: number } | undefined;

  constructor() {
    this.dialog.afterOpened.subscribe((ref: MatDialogRef<unknown>): void => {
      this.remember(ref);
      ref.beforeClosed().subscribe((): void => {
        const title = this.remember(ref);
        if (title) {
          this.lastClosed = { title, at: Date.now() };
        }
      });
    });
  }

  /** E.g. ["Employees", "Edit employee"]. */
  where(): ReadonlyArray<string> {
    const parts: Array<string> = [];

    // The page is the shell's nav item for the current URL - the same name
    // the top bar shows. Outside the shell (login) there is none.
    const pageKey = pageLabelKeyFor(this.router.url);
    if (pageKey) {
      const label: string = this.translate.instant(pageKey);
      if (label && label !== pageKey) {
        parts.push(label);
      }
    }

    // The first open dialog is the task; one on top of it is usually a
    // confirmation for that same task.
    const openTitle = this.dialog.openDialogs
      .map((ref: MatDialogRef<unknown>): string | undefined =>
        this.remember(ref)
      )
      .find(Boolean);
    const recentTitle =
      this.lastClosed && Date.now() - this.lastClosed.at < RECENTLY_CLOSED_MS
        ? this.lastClosed.title
        : undefined;
    const dialogTitle = openTitle ?? recentTitle;
    if (dialogTitle && dialogTitle !== parts[0]) {
      parts.push(dialogTitle);
    }

    return parts;
  }

  /** Reads the dialog's title off the page, keeping the last one seen. */
  private remember(ref: MatDialogRef<unknown>): string | undefined {
    const title = this.readTitle(ref.id);
    if (title) {
      this.titles.set(ref, title);
    }
    return this.titles.get(ref);
  }

  private readTitle(dialogId: string): string | undefined {
    const heading = this.document
      .getElementById(dialogId)
      ?.querySelector(".mat-mdc-dialog-title");
    if (!heading) {
      return undefined;
    }
    // Leave out the close button, whose icon ligature is text ("close").
    const copy = heading.cloneNode(true) as Element;
    copy
      .querySelectorAll("button, mat-icon")
      .forEach((node: Element): void => node.remove());
    const text = copy.textContent?.replace(/\s+/g, " ").trim();
    return text || undefined;
  }
}
